'use client';

import type { PropsWithChildren } from 'react';
import type { Collection } from '@/app/actions/collection';
import type { User } from '@/types/user/type';
import type { AnswerType } from '~/model/AnswerType';
import type { ContributionType } from '~/model/ContributionType';
import type { UserInteractionStatusType } from '~/model/InteractionType';
import { createContext, useContext } from 'react';
import { useContributor } from '@/app/hooks/useContributor';

const ContributorContext = createContext<ReturnType<typeof useContributor> | undefined>(undefined);

export const useContributorContext = () => {
  const context = useContext(ContributorContext);
  if (!context) {
    throw new Error(
      'useContributorContext must be called within ContributorContext',
    );
  }
  return context;
};

export const ContributorProvider = ({
  children,
  answers,
  contributor,
  contributions,
  status,
}: PropsWithChildren<{
  answers?: Collection<AnswerType>;
  contributor?: User;
  contributions?: Collection<ContributionType>;
  status?: UserInteractionStatusType;
}>) => {
  const value = useContributor({
    answers,
    contributions,
    contributor,
    status,
  });

  return (
    <ContributorContext.Provider value={value}>
      {children}
    </ContributorContext.Provider>
  );
};
